import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

interface WheelState {
  isRotating: boolean;
  wheelDegrees: number;
  rotationDuration: number;
  selectedLetterIndex: number | null;
  isFreeze: boolean;
  letterArray: string[];
  lastLetters: string[];
  currentSpinCount: number;
  targetWordLetters: string[];
  rotationQueue: string[];
  spinThreshold: number;
  letterPadding: number;
  isDemo: boolean;
  letterPool: string[];
  poolIndex: number;
}

export const useWordWheelStore = defineStore('wordWheel', () => {
  // State
  const isRotating = ref(false);
  const wheelDegrees = ref(0);
  const rotationDuration = ref(1.0);
  const selectedLetterIndex = ref<number | null>(null);
  const isFreeze = ref(false);

  // Letter Sequence State
  const letterArray = ref<string[]>([]);
  const lastLetters = ref<string[]>([]);
  const currentSpinCount = ref(0);
  const targetWordLetters = ref<string[]>([]);
  const rotationQueue = ref<string[]>([]);
  const spinThreshold = ref(0);
  const letterPadding = ref(0);
  const isDemo = ref(false);

  // Letter Pool State
  const letterPool = ref<string[]>([]);
  const poolIndex = ref(0);

  // Letter Banks
  const consonantBank = ['M', 'N', 'S', 'G', 'D', 'H', 'F', 'R', 'T', 'B', 'W'];
  const vowelBank = ['A', 'O', 'I', 'U', 'E', 'Y'];

  // Computed
  const computedLetterArray = computed(() => {
    return letterArray.value.slice(0, 12);
  });

  const isAnimating = ref(false);
  const animationDuration = ref(0);
  const animationPromise = ref<Promise<void> | null>(null);
  const animationResolve = ref<
    ((value: void | PromiseLike<void>) => void) | null
  >(null);

  // Start animation and return promise
  function startAnimation(duration: number) {
    if (isAnimating.value) return animationPromise.value;

    isAnimating.value = true;
    animationDuration.value = duration;

    animationPromise.value = new Promise<void>((resolve) => {
      animationResolve.value = resolve;
    });

    return animationPromise.value;
  }

  // Complete animation and resolve promise
  function completeAnimation() {
    if (animationResolve.value) {
      animationResolve.value();
      animationResolve.value = null;
      animationPromise.value = null;
      isAnimating.value = false;
    }
  }

  // Cleanup animation state
  function resetAnimation(preserveLetters = false) {
    // Always reset these animation-specific states
    isAnimating.value = false;
    animationDuration.value = 0;
    animationPromise.value = null;
    animationResolve.value = null;

    // If preserveLetters is false (default), also reset letter-related state
    if (!preserveLetters) {
      // Reset any state that might affect letter display
      // DO NOT reset letterArray itself

      // Only reset selection state, which might be affecting display
      selectedLetterIndex.value = null;
    }
    // When preserveLetters is true, we keep the letterArray and selectedLetterIndex intact
  }
  // Actions
  function startRotation(duration: number) {
    isRotating.value = true;
    rotationDuration.value = duration;
    selectedLetterIndex.value = null; // Clear selected letter during rotation
  }

  function stopRotation() {
    isRotating.value = false;
    console.log('Rotation stopped');
  }

  function setWheelDegrees(degrees: number) {
    wheelDegrees.value = degrees;
  }

  function setIsDemo(isDemo: boolean) {
    isDemo.value = isDemo;
  }

  function setLetterArray(letters: string[]) {
    letterArray.value = letters;
  }

  function updateLastLetters(letter: string) {
    // append letter to lastLetters array
    lastLetters.value = [...lastLetters.value, letter];
  }

  const visibleLetters = ref<string[]>([]);

  function rotateVisibleLetters() {
    if (visibleLetters.value.length > 0) {
      visibleLetters.value.push(visibleLetters.value.shift()!);
    }
  }

  function addVisibleLetter(letter: string) {
    visibleLetters.value.push(letter);
    if (visibleLetters.value.length > 12) {
      visibleLetters.value.shift();
    }
  }

  function addVisibleLetters(letters: string[]) {
    visibleLetters.value.push(...letters);
    while (visibleLetters.value.length > 12) {
      visibleLetters.value.shift();
    }
  }

  function incrementSpinCount() {
    currentSpinCount.value++;
  }

  function resetSpinCount() {
    currentSpinCount.value = 0;
  }

  function setSelectedLetterIndex(index: number | null) {
    selectedLetterIndex.value = index;
  }

  function setTargetWordLetters(letters: string[]) {
    targetWordLetters.value = [...letters]; // Use array copy to avoid reference issues
  }

  function setFreeze(value: boolean) {
    isFreeze.value = value;
  }

  function buildLetterArray() {
    // Build a complete new 12-letter array from the safe pool
    const newLetters = drawFromPool(12);
    setLetterArray(newLetters);
    console.log('Built letter array from safe pool:', newLetters.join(''));
  }

  function replenishLetterArray() {
    // Keep the array length at 12 by drawing from the safe pool
    while (letterArray.value.length < 12) {
      const lettersNeeded = 12 - letterArray.value.length;
      const newLetters = drawFromPool(lettersNeeded);
      letterArray.value.push(...newLetters);
    }

    // Ensure we only keep 12 letters max
    if (letterArray.value.length > 12) {
      letterArray.value = letterArray.value.slice(0, 12);
    }

    console.log(
      'Replenished letter array from safe pool:',
      letterArray.value.join('')
    );
  }

  // A simpler, more deterministic approach to letter sequence management
  function shiftLetterAndReplenish(forceTargetLetter = false) {
    console.log('==== SHIFT AND REPLENISH ====');
    console.log('Current spin count:', currentSpinCount.value);
    console.log('Spin threshold:', spinThreshold.value);
    console.log('Target word letters:', targetWordLetters.value);
    console.log('Before shift: Letter array =', letterArray.value);
    console.log('Force target letter?', forceTargetLetter);

    // STEP 1: Remove the first letter from the array
    if (letterArray.value.length > 0) {
      console.log(
        'First few letters before shift:',
        letterArray.value.slice(0, 3)
      );
      const removedLetter = letterArray.value.shift();
      console.log('Removed letter:', removedLetter);
      console.log(
        'First few letters after shift:',
        letterArray.value.slice(0, 3)
      );
    } else {
      console.error(
        'Letter array is empty before shift. This should not happen.'
      );
    }

    // STEP 2: Determine if this spin should add a target word letter
    // Now we use the forceTargetLetter parameter to control this behavior from outside
    const readyForTargetLetter =
      (forceTargetLetter || currentSpinCount.value >= spinThreshold.value) &&
      targetWordLetters.value &&
      targetWordLetters.value.length > 0;

    // STEP 3: Add the appropriate letter to the end of the array
    let newLetter = '';

    if (readyForTargetLetter) {
      // Safety check: ensure we have target letters available
      if (targetWordLetters.value.length === 0) {
        console.error('Target letters depleted! Cannot add target letter.');
        console.log('This usually indicates a bug in letter management.');

        // Fallback: draw from pool instead of crashing
        const poolLetters = drawFromPool(1);
        newLetter = poolLetters[0];
        console.log('Adding fallback letter from pool:', newLetter);
      } else {
        newLetter = targetWordLetters.value[0];
        targetWordLetters.value.shift();
        console.log('✓ ADDING TARGET LETTER:', newLetter);
        console.log('Remaining target letters:', targetWordLetters.value);
      }
    } else {
      // Draw a safe letter from the pre-generated pool
      const poolLetters = drawFromPool(1);
      newLetter = poolLetters[0];
      console.log('Adding letter from pool:', newLetter);
    }

    letterArray.value.unshift(newLetter);

    // STEP 4: Ensure we have exactly 12 letters
    while (letterArray.value.length < 12) {
      const poolLetters = drawFromPool(1);
      letterArray.value.push(poolLetters[0]);
    }

    // Trim if over 12 (shouldn't happen, but just in case)
    if (letterArray.value.length > 12) {
      console.warn('Letter array exceeded 12 letters. Trimming.');
      letterArray.value = letterArray.value.slice(0, 12);
    }

    console.log('After shift and replenish: Letter array =', letterArray.value);
    console.log('==== END SHIFT AND REPLENISH ====');
  }

  function isLastTargetLetter(letter: string): boolean {
    if (targetWordLetters.value.length === 0) return false;
    return (
      letter.toUpperCase() ===
      targetWordLetters.value[targetWordLetters.value.length - 1].toUpperCase()
    );
  }

  function generateLetterSequence(includeTargetLetter = false) {
    let newLetterArray = [...letterArray.value];

    if (includeTargetLetter && targetWordLetters.value.length > 0) {
      const nextLetter = targetWordLetters.value[0];
      newLetterArray[2] = nextLetter;
      targetWordLetters.value.shift();
    } else {
      const letterBank = Math.random() < 0.3 ? vowelBank : consonantBank;
      newLetterArray[2] =
        letterBank[Math.floor(Math.random() * letterBank.length)];
    }

    for (let i = 3; i < 12; i++) {
      const bank = Math.random() < 0.3 ? vowelBank : consonantBank;
      newLetterArray[i] = bank[Math.floor(Math.random() * bank.length)];
    }

    setLetterArray(newLetterArray);
    return newLetterArray;
  }

  function queueRotation(removeFirstLetter: boolean) {
    rotationQueue.value.push(removeFirstLetter.toString());
  }

  function dequeueRotation() {
    return rotationQueue.value.shift() === 'true';
  }

  function clearRotationQueue() {
    rotationQueue.value = [];
  }

  function setSpinThreshold(value: number) {
    spinThreshold.value = value;
  }

  function setLetterPadding(value: number) {
    letterPadding.value = value;
  }

  /**
   * Initialize the letter pool with safe letters
   * @param pool - Pre-generated array of safe letters
   */
  function initializeLetterPool(pool: string[]) {
    letterPool.value = [...pool];
    poolIndex.value = 0;
    console.log(`Letter pool initialized with ${pool.length} letters`);
  }

  /**
   * Draw letters from the pool
   * @param count - Number of letters to draw
   * @returns Array of letters drawn from pool
   */
  function drawFromPool(count: number): string[] {
    const letters: string[] = [];

    for (let i = 0; i < count; i++) {
      // Check if we need to wrap around or if pool is exhausted
      if (poolIndex.value >= letterPool.value.length) {
        console.warn('Letter pool exhausted, wrapping to beginning');
        poolIndex.value = 0;
      }

      if (letterPool.value.length === 0) {
        console.error('Letter pool is empty!');
        // Fallback to random letter
        const useVowel = Math.random() < 0.3;
        const bank = useVowel ? vowelBank : consonantBank;
        letters.push(bank[Math.floor(Math.random() * bank.length)]);
      } else {
        letters.push(letterPool.value[poolIndex.value]);
        poolIndex.value++;
      }
    }

    return letters;
  }

  /**
   * Get remaining letters in pool
   */
  function getRemainingPoolSize(): number {
    return letterPool.value.length - poolIndex.value;
  }

  function reset() {
    console.log('Resetting WordWheel store state');
    // Animation state
    isRotating.value = false;
    wheelDegrees.value = 0;
    rotationDuration.value = 1.0;
    isAnimating.value = false;
    animationDuration.value = 0;
    animationPromise.value = null;
    animationResolve.value = null;

    // Selection state
    selectedLetterIndex.value = null;
    isFreeze.value = false;

    // Letter arrays
    letterArray.value = [];
    lastLetters.value = [];
    visibleLetters.value = [];

    // Game state
    currentSpinCount.value = 0;
    targetWordLetters.value = [];
    rotationQueue.value = [];

    // Configuration
    spinThreshold.value = 0;
    letterPadding.value = 0;

    // Letter pool
    letterPool.value = [];
    poolIndex.value = 0;

    console.log('WordWheel store reset complete');
  }

  return {
    // State
    isRotating,
    wheelDegrees,
    rotationDuration,
    letterArray,
    lastLetters,
    currentSpinCount,
    selectedLetterIndex,
    isFreeze,
    rotationQueue,
    spinThreshold,
    letterPadding,
    isDemo,

    // Computed
    computedLetterArray,

    // Animation Actions
    startAnimation,
    completeAnimation,
    resetAnimation,
    isAnimating,
    animationDuration,

    // Wheel Actions
    startRotation,
    stopRotation,
    setWheelDegrees,

    // Letter Management
    setLetterArray,
    updateLastLetters,
    buildLetterArray,
    replenishLetterArray,
    shiftLetterAndReplenish,
    generateLetterSequence,

    visibleLetters,
    rotateVisibleLetters,
    addVisibleLetter,
    addVisibleLetters,

    // Game State Management
    incrementSpinCount,
    resetSpinCount,
    setSelectedLetterIndex,
    setTargetWordLetters,
    setFreeze,

    // Rotation Queue Management
    queueRotation,
    dequeueRotation,
    clearRotationQueue,
    setIsDemo,
    // Configuration
    setSpinThreshold,
    setLetterPadding,
    isLastTargetLetter,

    // Letter Pool Management
    initializeLetterPool,
    drawFromPool,
    getRemainingPoolSize,
    letterPool,
    poolIndex,

    // Reset
    reset,
  };
});
